import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigFormFooter } from './ConfigFormFooter';
import {
  buildFxRecord,
  fxFormSeed,
  minskDateOf,
  minskToday,
  readFx,
  type FxFormValues,
} from '../_lib/fxEdit';
import type { RawRecord } from '../_lib/rawRecord';

/**
 * AUT-542 — `specialists.fx` form: the current rate with its source and
 * date, then «BYN за 1 USD», «BYN за 1 EUR» (optional), «Дата курса».
 * `null` stored → «Курс не задан» and an empty form. Saves a copy of the
 * stored record (RULINGS 11) built by `buildFxRecord`.
 */

const RATE_FORMAT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4 });

export interface FxFormProps {
  /** The stored record, or `null` for «Курс не задан». */
  stored: RawRecord | null;
  busy: boolean;
  error?: string | undefined;
  onSave: (record: RawRecord) => void;
  onCancel: () => void;
}

const Kv = ({ label, value }: { label: string; value: string }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{value}</Typography>
  </Box>
);

export const FxForm = ({ stored, busy, error, onSave, onCancel }: FxFormProps): ReactElement => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FxFormValues>(() => fxFormSeed(stored));
  const now = new Date();
  const record = buildFxRecord(stored, form, now);
  const current = readFx(stored);

  const field = (name: keyof FxFormValues) => ({
    value: form[name],
    disabled: busy,
    onChange: (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value })),
  });

  return (
    <Box data-testid="config-fx-form" sx={{ py: 1.5 }}>
      {current ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 4, mb: 2 }}>
          <Kv label={t('config.fx.usd')} value={RATE_FORMAT.format(current.bynPerUsd)} />
          <Kv
            label={t('config.fx.eur')}
            value={
              current.bynPerEur === undefined
                ? t('config.noValue')
                : RATE_FORMAT.format(current.bynPerEur)
            }
          />
          <Kv
            label={t('config.fx.asOf')}
            value={dayjs(minskDateOf(current.asOf)).format('DD.MM.YYYY')}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {t('config.fx.source')}
            </Typography>
            <Chip size="small" label={t(`config.fx.sources.${current.source}`)} />
          </Box>
        </Box>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t('config.fx.unset')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label={t('config.fx.usd')}
          {...field('usd')}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        />
        <TextField
          size="small"
          label={t('config.fx.eur')}
          {...field('eur')}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        />
        <TextField
          size="small"
          type="date"
          label={t('config.fx.asOf')}
          {...field('asOf')}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: minskToday(now) } }}
        />
      </Box>

      <ConfigFormFooter
        canSave={record !== null}
        busy={busy}
        error={error}
        onSave={() => {
          if (record) onSave(record);
        }}
        onCancel={onCancel}
      />
    </Box>
  );
};
